import React, { useState, useRef, useEffect } from 'react';
import type { Page, NavParams } from '@types';

// The items passed from Header.tsx have different shapes
interface DropdownItem {
    title: string;
    page?: Page;
    params?: NavParams;
    action?: () => void;
}

interface NavigationDropdownProps {
    title: string;
    items: DropdownItem[];
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
}

const NavigationDropdown: React.FC<NavigationDropdownProps> = ({ title, items, onNavigate }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleItemClick = (item: DropdownItem) => {
        if (item.action) {
            item.action();
        } else if (item.page) {
            onNavigate({ page: item.page, params: item.params });
        }
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-white bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
            >
                {title}
                <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        {items.map((item) => (
                            <button
                                key={item.title}
                                onClick={() => handleItemClick(item)}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                role="menuitem"
                            >
                                {item.title}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NavigationDropdown;
